import { useState, useEffect, useCallback } from 'react';
import { builderApi } from '../api';

// ── system defaults (always available, cannot be deleted) ────────────────────
const DEFAULT_CATS  = ['Aggregate', 'Bricks', 'Cement', 'Concrete', 'Electrical', 'Glass',
                       'Paint', 'Plumbing', 'Sand', 'Steel', 'Tiles', 'Wood', 'Other'];
const DEFAULT_UNITS = ['Bags', 'Cubic Feet', 'Cubic Meter', 'Kg', 'Liters', 'Nos',
                       'Numbers', 'Pieces', 'Running Ft', 'Sq.Ft', 'Thousand Numbers', 'Tonnes'];

const LOOKUP_CAT  = 'material_category';
const LOOKUP_UNIT = 'unit_type';

const EMPTY_FORM = (cat) => ({ name: '', category: cat || 'Cement', unit: 'Bags' });

const ErrBanner = ({ msg, onClose }) => msg ? (
  <div style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 8,
    padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
    <span style={{ fontSize: 18 }}>⚠️</span>
    <span style={{ color: '#c53030', fontSize: 13, fontWeight: 600, flex: 1 }}>{msg}</span>
    <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
  </div>
) : null;

const fieldErr = fe => k => fe[k] ? { borderColor: '#e53e3e', background: '#fff5f5' } : {};
const selStyle = {
  border: '1px solid #3182ce', borderRadius: 6, padding: '4px 8px',
  fontSize: 13, background: '#ebf8ff', color: '#2b6cb0', cursor: 'pointer', outline: 'none',
};

// ── merge default list with DB custom list (deduplicate by value) ─────────────
function mergeList(defaults, custom) {
  const all = [...defaults];
  custom.forEach(c => { if (!all.includes(c.value)) all.push(c.value); });
  return all.sort((a, b) => a.localeCompare(b));
}

// ── Metadata management panel ─────────────────────────────────────────────────
function MetadataPanel({ onCatsChanged, onUnitsChanged }) {
  const [catLookups,  setCatLookups]  = useState([]);
  const [unitLookups, setUnitLookups] = useState([]);
  const [newCat,  setNewCat]  = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [saving,  setSaving]  = useState({});
  const [err,     setErr]     = useState('');

  const loadLookups = useCallback(() => {
    Promise.all([
      builderApi.getLookups(LOOKUP_CAT),
      builderApi.getLookups(LOOKUP_UNIT),
    ]).then(([cats, units]) => {
      setCatLookups(cats);
      setUnitLookups(units);
      onCatsChanged(cats);
      onUnitsChanged(units);
    }).catch(e => setErr(e.message));
  }, [onCatsChanged, onUnitsChanged]);

  useEffect(() => { loadLookups(); }, [loadLookups]);

  const addLookup = async (category, value, setter) => {
    const v = value.trim();
    if (!v) return;
    setSaving(s => ({ ...s, [category]: true }));
    try {
      await builderApi.createLookup({ category, value: v });
      setter('');
      loadLookups();
    } catch (e) { setErr(e.message); }
    finally { setSaving(s => ({ ...s, [category]: false })); }
  };

  const delLookup = async (id) => {
    try {
      await builderApi.deleteLookup(id);
      loadLookups();
    } catch (e) { setErr(e.message); }
  };

  const MetaSection = ({ title, icon, defaults, lookups, newVal, setNewVal, lookupKey }) => (
    <div style={{ background: '#fff', border: '1px solid var(--border-color)', borderRadius: 10, padding: 18, flex: 1, minWidth: 260 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 14 }}>
        {icon} {title}
      </div>

      {/* Add new */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          className="form-input"
          value={newVal}
          onChange={e => setNewVal(e.target.value)}
          placeholder={`New ${title.toLowerCase()}…`}
          onKeyDown={e => { if (e.key === 'Enter') addLookup(lookupKey, newVal, setNewVal); }}
          style={{ flex: 1 }}
        />
        <button
          className="btn btn-primary"
          disabled={!newVal.trim() || saving[lookupKey]}
          onClick={() => addLookup(lookupKey, newVal, setNewVal)}
          style={{ whiteSpace: 'nowrap', fontSize: 13 }}
        >
          {saving[lookupKey] ? '…' : '+ Add'}
        </button>
      </div>

      {/* Custom values (deletable) */}
      {lookups.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#718096', textTransform: 'uppercase',
            letterSpacing: '0.06em', marginBottom: 6 }}>Custom</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {lookups.map(l => (
              <span key={l.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
                background: '#e9f5ff', border: '1px solid #bee3f8', borderRadius: 20,
                padding: '3px 10px', fontSize: 13, color: '#2b6cb0', fontWeight: 500 }}>
                {l.value}
                <button
                  onClick={() => delLookup(l.id)}
                  title="Remove"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53e3e',
                    fontWeight: 700, fontSize: 14, lineHeight: 1, padding: 0, marginLeft: 2 }}
                >×</button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* System defaults (read-only) */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#718096', textTransform: 'uppercase',
          letterSpacing: '0.06em', marginBottom: 6 }}>System Defaults</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {defaults.map(d => (
            <span key={d} style={{ background: '#f7fafc', border: '1px solid #e2e8f0',
              borderRadius: 20, padding: '3px 10px', fontSize: 12, color: '#718096' }}>
              {d}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ padding: 20 }}>
      <ErrBanner msg={err} onClose={() => setErr('')} />
      <div style={{ fontSize: 13, color: '#718096', marginBottom: 16, lineHeight: 1.5 }}>
        Add custom categories and unit types here. They appear in the material form dropdowns alongside the system defaults.
        Custom values can be removed; system defaults are always available.
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <MetaSection
          title="Material Categories" icon="🏷️"
          defaults={DEFAULT_CATS}
          lookups={catLookups}
          newVal={newCat} setNewVal={setNewCat}
          lookupKey={LOOKUP_CAT}
        />
        <MetaSection
          title="Unit Types" icon="📐"
          defaults={DEFAULT_UNITS}
          lookups={unitLookups}
          newVal={newUnit} setNewVal={setNewUnit}
          lookupKey={LOOKUP_UNIT}
        />
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function BuilderMaterials({ user }) {
  const isOwner = user?.role === 'role_owner';
  const [materials,   setMaterials]   = useState([]);
  const [customCats,  setCustomCats]  = useState([]);
  const [customUnits, setCustomUnits] = useState([]);
  const [activeCat,   setActiveCat]   = useState('ALL');
  const [form,        setForm]        = useState(EMPTY_FORM(null));
  const [showForm,    setShowForm]    = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [err,         setErr]         = useState('');
  const [fe,          setFe]          = useState({});
  const [editId,      setEditId]      = useState(null);
  const [editRow,     setEditRow]     = useState({});
  const [tab,         setTab]         = useState('catalog');

  // Merged lists used in dropdowns
  const allCats  = mergeList(DEFAULT_CATS,  customCats);
  const allUnits = mergeList(DEFAULT_UNITS, customUnits);

  const load = () => {
    builderApi.getMaterials()
      .then(setMaterials)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const counts = materials.reduce((acc, m) => {
    const c = m.category || 'Other';
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {});

  const displayed = activeCat === 'ALL' ? materials : materials.filter(m => (m.category || 'Other') === activeCat);

  const openForm = () => {
    setForm(EMPTY_FORM(activeCat === 'ALL' ? allCats[0] : activeCat));
    setShowForm(true); setFe({}); setErr('');
  };

  const save = async () => {
    const errors = {};
    if (!form.name.trim()) errors.name = true;
    setFe(errors);
    if (Object.keys(errors).length > 0) { setErr('Material Name is required'); return; }
    try {
      await builderApi.createMaterial(form);
      setShowForm(false); setForm(EMPTY_FORM(activeCat === 'ALL' ? allCats[0] : activeCat)); setFe({});
      load();
    } catch (e) { setErr(e.message); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this material?')) return;
    try { await builderApi.deleteMaterial(id); load(); } catch (e) { setErr(e.message); }
  };

  const startEdit = (m) => {
    setEditId(m.id);
    setEditRow({ name: m.name, category: m.category || 'Other', unit: m.unit });
  };

  const cancelEdit = () => { setEditId(null); setEditRow({}); };

  const saveEdit = async (id) => {
    if (!editRow.name?.trim()) { setErr('Material Name is required'); return; }
    try {
      await builderApi.updateMaterial(id, editRow);
      setEditId(null); setEditRow({});
      load();
    } catch (e) { setErr(e.message); }
  };

  const f   = (k, v) => { setForm(p => ({ ...p, [k]: v })); if (fe[k]) setFe(p => ({ ...p, [k]: false })); };
  const inv = fieldErr(fe);

  const selectCat = (cat) => { setActiveCat(cat); setEditId(null); setShowForm(false); setErr(''); };

  const TAB_STYLE = (key) => ({
    background: 'none', border: 'none', cursor: 'pointer', padding: '10px 22px', fontSize: 14, fontWeight: 600,
    color: tab === key ? '#3182ce' : '#718096',
    borderBottom: tab === key ? '2px solid #3182ce' : '2px solid transparent', marginBottom: -2,
  });

  return (
    <div className="bld-card" style={{ padding: 0, overflow: 'hidden' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 20px', borderBottom: '1px solid var(--border-color)' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>
          Material Catalog
          {tab === 'catalog' && activeCat !== 'ALL' && (
            <span style={{ marginLeft: 10, fontSize: 13, fontWeight: 500, color: '#3182ce',
              background: '#ebf8ff', padding: '2px 10px', borderRadius: 12 }}>{activeCat}</span>
          )}
        </h2>
        {tab === 'catalog' && (
          <button className="btn btn-primary"
            onClick={() => showForm ? (setShowForm(false), setFe({}), setErr('')) : openForm()}>
            {showForm ? '✕ Cancel' : '+ Add Material'}
          </button>
        )}
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--border-color)', padding: '0 20px' }}>
        <button style={TAB_STYLE('catalog')}  onClick={() => setTab('catalog')}>📦 Materials</button>
        <button style={TAB_STYLE('metadata')} onClick={() => { setTab('metadata'); setShowForm(false); }}>⚙️ Categories &amp; Units</button>
      </div>

      {/* ── Metadata tab ── */}
      {tab === 'metadata' && (
        <MetadataPanel
          onCatsChanged={setCustomCats}
          onUnitsChanged={setCustomUnits}
        />
      )}

      {/* ── Catalog tab ── */}
      {tab === 'catalog' && (
        <>
          {/* Add form */}
          {showForm && (
            <div style={{ padding: '16px 20px', background: '#f7fafc', borderBottom: '1px solid var(--border-color)' }}>
              <ErrBanner msg={err} onClose={() => setErr('')} />
              <div style={{ fontWeight: 700, fontSize: 13, color: '#4a5568', marginBottom: 12 }}>New Material</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 180px', gap: 12, alignItems: 'start' }}>
                <div>
                  <label className="form-label">Material Name *</label>
                  <input className="form-input" style={inv('name')} value={form.name}
                    onChange={e => f('name', e.target.value)} placeholder="e.g. OPC Cement 53 Grade" autoFocus />
                  {fe.name && <div style={{ color: '#e53e3e', fontSize: 11, marginTop: 3 }}>Required</div>}
                </div>
                <div>
                  <label className="form-label">Category</label>
                  <select className="form-input" value={form.category} onChange={e => f('category', e.target.value)}>
                    {allCats.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Unit</label>
                  <select className="form-input" value={form.unit} onChange={e => f('unit', e.target.value)}>
                    {allUnits.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button className="btn btn-primary" onClick={save}>Add Material</button>
                <button className="btn btn-outline" onClick={() => { setShowForm(false); setFe({}); setErr(''); }}>Cancel</button>
              </div>
            </div>
          )}

          {!showForm && err && (
            <div style={{ padding: '0 20px' }}>
              <ErrBanner msg={err} onClose={() => setErr('')} />
            </div>
          )}

          {/* Split layout */}
          <div style={{ display: 'flex', minHeight: 300 }}>

            {/* Left: category filter */}
            <div style={{ width: 170, flexShrink: 0, borderRight: '1px solid var(--border-color)',
              padding: '12px 8px', background: '#f7fafc' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#718096', textTransform: 'uppercase',
                letterSpacing: '0.08em', padding: '0 8px 8px' }}>
                Filter by Category
              </div>

              <button onClick={() => selectCat('ALL')} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', padding: '7px 10px', borderRadius: 7, border: 'none',
                background: activeCat === 'ALL' ? '#3182ce' : 'transparent',
                color: activeCat === 'ALL' ? '#fff' : '#4a5568',
                fontWeight: activeCat === 'ALL' ? 700 : 500,
                fontSize: 13, cursor: 'pointer', marginBottom: 2, textAlign: 'left',
              }}>
                <span>All</span>
                <span style={{ fontSize: 11, fontWeight: 700,
                  background: activeCat === 'ALL' ? 'rgba(255,255,255,.25)' : '#e2e8f0',
                  color: activeCat === 'ALL' ? '#fff' : '#718096',
                  borderRadius: 10, padding: '1px 7px', minWidth: 22, textAlign: 'center',
                }}>{materials.length}</span>
              </button>

              {Object.keys(counts).sort().map(cat => (
                <button key={cat} onClick={() => selectCat(cat)} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', padding: '7px 10px', borderRadius: 7, border: 'none',
                  background: activeCat === cat ? '#3182ce' : 'transparent',
                  color: activeCat === cat ? '#fff' : '#4a5568',
                  fontWeight: activeCat === cat ? 700 : 500,
                  fontSize: 13, cursor: 'pointer', marginBottom: 2, textAlign: 'left',
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, flexShrink: 0, marginLeft: 4,
                    background: activeCat === cat ? 'rgba(255,255,255,.25)' : '#e2e8f0',
                    color: activeCat === cat ? '#fff' : '#718096',
                    borderRadius: 10, padding: '1px 7px', minWidth: 22, textAlign: 'center',
                  }}>{counts[cat]}</span>
                </button>
              ))}
            </div>

            {/* Right: materials table */}
            <div style={{ flex: 1, overflowX: 'auto' }}>
              {loading ? (
                <div style={{ color: '#718096', fontSize: 13, padding: 24 }}>Loading…</div>
              ) : displayed.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#718096', padding: 48, fontSize: 14 }}>
                  {materials.length === 0
                    ? <>No materials yet. Click <strong>+ Add Material</strong> to get started.</>
                    : <>No materials in <strong>{activeCat}</strong>.</>}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f7fafc' }}>
                      <th style={{ padding: '9px 16px', textAlign: 'left', fontWeight: 700, fontSize: 11,
                        color: '#718096', textTransform: 'uppercase', letterSpacing: '0.06em',
                        borderBottom: '2px solid var(--border-color)', width: '45%' }}>Material Name</th>
                      {activeCat === 'ALL' && (
                        <th style={{ padding: '9px 16px', textAlign: 'left', fontWeight: 700, fontSize: 11,
                          color: '#718096', textTransform: 'uppercase', letterSpacing: '0.06em',
                          borderBottom: '2px solid var(--border-color)', width: '20%' }}>Category</th>
                      )}
                      <th style={{ padding: '9px 16px', textAlign: 'center', fontWeight: 700, fontSize: 11,
                        color: '#718096', textTransform: 'uppercase', letterSpacing: '0.06em',
                        borderBottom: '2px solid var(--border-color)', width: '20%' }}>Unit</th>
                      <th style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 700, fontSize: 11,
                        color: '#718096', textTransform: 'uppercase', letterSpacing: '0.06em',
                        borderBottom: '2px solid var(--border-color)', width: '15%' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayed.map((m, idx) => {
                      const isEditing = editId === m.id;
                      const stripe    = idx % 2 !== 0 ? '#f9fafb' : '#fff';
                      return (
                        <tr key={m.id} style={{ background: stripe }}>
                          {isEditing ? (
                            <>
                              <td style={{ padding: '7px 16px', borderBottom: '1px solid var(--border-color)' }}>
                                <input className="form-input" value={editRow.name} autoFocus
                                  onChange={e => setEditRow(r => ({ ...r, name: e.target.value }))}
                                  style={{ padding: '5px 8px', fontSize: 13 }} />
                              </td>
                              {activeCat === 'ALL' && (
                                <td style={{ padding: '7px 16px', borderBottom: '1px solid var(--border-color)' }}>
                                  <select style={selStyle} value={editRow.category}
                                    onChange={e => setEditRow(r => ({ ...r, category: e.target.value }))}>
                                    {allCats.map(c => <option key={c} value={c}>{c}</option>)}
                                  </select>
                                </td>
                              )}
                              <td style={{ padding: '7px 16px', borderBottom: '1px solid var(--border-color)', textAlign: 'center' }}>
                                <select style={selStyle} value={editRow.unit}
                                  onChange={e => setEditRow(r => ({ ...r, unit: e.target.value }))}>
                                  {allUnits.map(u => <option key={u} value={u}>{u}</option>)}
                                </select>
                              </td>
                              <td style={{ padding: '7px 16px', borderBottom: '1px solid var(--border-color)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                <button onClick={() => saveEdit(m.id)} style={{ background: '#c6f6d5', color: '#276749', border: '1px solid #9ae6b4', padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 6 }}>Save</button>
                                <button onClick={cancelEdit} style={{ background: '#f7fafc', color: '#4a5568', border: '1px solid #cbd5e0', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-color)', fontWeight: 500 }}>
                                {m.name}
                              </td>
                              {activeCat === 'ALL' && (
                                <td style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-color)' }}>
                                  <span style={{ background: '#f0fff4', color: '#276749', padding: '2px 9px',
                                    borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                                    {m.category || 'Other'}
                                  </span>
                                </td>
                              )}
                              <td style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-color)', textAlign: 'center' }}>
                                <span style={{ background: '#ebf8ff', color: '#2b6cb0', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}>
                                  {m.unit}
                                </span>
                              </td>
                              <td style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-color)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                <button onClick={() => startEdit(m)} style={{ background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 6 }}>Edit</button>
                                {isOwner && (
                                  <button onClick={() => del(m.id)} style={{ background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2', padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
                                )}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
